"use client";

import { useState } from "react";
import { cn } from "utils";

import { LoadingButton } from "@components/EmailLogin/LoadingButton";

import { InviteCodeEntryRow } from "./InviteCodeEntryRow";
import { InviteCodeInputField } from "./InviteCodeInputField";
import { createInviteCodeCopy } from "./inviteCodeCopy";
import { useInviteCodeApply } from "./useInviteCodeApply";

/**
 * 邀请码折叠式就地输入（inline disclosure）—— **PC 专用**，不跳路由、不弹窗。
 *
 * ```
 * 折叠： Have a referral code?              ← InviteCodeEntryRow 三态
 *        ✓ Referral code XXX applied
 *        ⓘ Referral code XXX not applied
 *          ↓ 点击（同一位置就地展开）
 * 展开： [ e.g. LXmWwn        ]  输入框（预填已捕获的码）
 *        [    Apply code      ]  提交 → 落盘 → 自动收起 → 行切到 applied 态
 * ```
 *
 * ## 为什么 PC 折叠、H5 不折叠
 *
 * 折叠在 PC 上零压力（内容列 420px、屏高 900+，展开只多约 108px）。H5 上不行 ——
 * 视口 664 时展开后 CTA 顶部已到约 608px，而 `SignInPageLayout` 是 `h-dvh + overflow-hidden`
 * **没有滚动容器**，键盘一弹可用高度掉到约 330px，内容直接被裁掉、点不到 Apply。
 * 原生同理，所以 H5 与原生都用全屏页（{@link import("./InviteCodeStageView").InviteCodeStageView}）。
 *
 * PC 也不能沿用「跳独立页」：`/invite-code` 与 `/login` 同在 `(root)` 路由组，而
 * `RootLayoutClient.LANDING_PAGE_ROUTES` 曾漏了它 —— PC 上会渲染出完整产品导航侧栏，
 * 未登录用户点进去又被 307 踢回 `/login`；整页导航还会把已填的邮箱与协议勾选一起丢掉。
 *
 * 视觉复用 Figma `104980:320350` 的元件与文案（输入框 `104980:320357`、
 * Apply 按钮 `Button / General` `104980:320360`、文案见 `inviteCodeCopy`）；
 * 该稿画的是全屏页，此处只改承载位置，不新造元件。判定逻辑见 {@link useInviteCodeApply}。
 */
interface InviteCodeSectionProps {
  className?: string;
  locale?: string;
}

export function InviteCodeSection({
  className,
  locale,
}: InviteCodeSectionProps) {
  const copy = createInviteCodeCopy(locale);
  const [expanded, setExpanded] = useState(false);
  const invite = useInviteCodeApply({ onDone: () => setExpanded(false) });

  const handleExpand = () => {
    invite.reset();
    setExpanded(true);
  };

  if (!expanded) {
    return (
      <InviteCodeEntryRow
        className={className}
        locale={locale}
        onClick={handleExpand}
      />
    );
  }

  return (
    <div
      className={cn("flex w-full flex-col gap-3", className)}
      data-testid="invite-code-section-expanded"
    >
      <InviteCodeInputField
        value={invite.value}
        onValueChange={invite.onValueChange}
        error={invite.error}
        locale={locale}
      />
      <LoadingButton
        type="button"
        disabled={!invite.canSubmit}
        loading={invite.checking}
        onClick={() => void invite.apply()}
        data-openreplay-label="login_invitecode_apply_click"
        data-testid="invite-code-apply"
        className="button md_button primary_button button_l flex h-12 w-full items-center justify-center rounded-lg text-content_inverse_primary"
      >
        {copy.applyCode}
      </LoadingButton>
    </div>
  );
}
